import { Stack } from '@mui/material'
import { useSetAtom } from 'jotai'
import { FC, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LocationChange } from '../../../api/models'
import { useLocationChanges } from '../../../api/apiQueries'
import { addressValidationResultAtom, currentLocationChangeAtom, selectedAddressValidationResultIndexAtom } from '../../../atoms'
import { Page } from '../../../components/Page'
import { AddressChangesList } from './AddressChangesList'
import { ScheduleNewAddressPanel } from './ScheduleNewAddressPanel'
import { PreviousChangesBox } from './PreviousChangesBox'
import { addressValidationPaths } from '../addressValidationPaths'

export const OverviewPage: FC = () => {
  const { sid: locationId } = useParams()
  const navigate = useNavigate()
  const locationChanges = useLocationChanges(locationId)
  const setCurrentLocationChange = useSetAtom(currentLocationChangeAtom)

  const setAddressValidationResults = useSetAtom(addressValidationResultAtom)
  const setSelectedAddressValidationResult = useSetAtom(selectedAddressValidationResultIndexAtom)

  useEffect(() => {
    setCurrentLocationChange(undefined)
    setAddressValidationResults(undefined)
    setSelectedAddressValidationResult(undefined)
  }, [setCurrentLocationChange, setAddressValidationResults, setSelectedAddressValidationResult])

  const hasPendingAddressChange = locationChanges.data?.some(change => change.state === 'pending')
  const hasNoPastAddressChanges = locationChanges.data?.length === 0

  const handleScheduleAddress = () => {
    setCurrentLocationChange({ locationId: locationId! })
    navigate(`../${addressValidationPaths.addressForm}`)
  }

  const handleScheduleEdit = (locationChange: LocationChange) => {
    setCurrentLocationChange(locationChange)
    navigate(`../${locationChange.id}/${addressValidationPaths.datePicker}`)
  }

  return (
    <Page title={'Address Change Scheduler'}>
      {!locationChanges.isLoading && (
        <Stack flexGrow={1} spacing={'2rem'} useFlexGap data-testid="address-scheduler-content">
          {!hasPendingAddressChange && <ScheduleNewAddressPanel onScheduleNewAddress={handleScheduleAddress} />}
          {hasNoPastAddressChanges && <PreviousChangesBox />}
          {locationChanges.data && locationChanges.data?.length > 0 && (
            <AddressChangesList onEditClicked={handleScheduleEdit} addressChanges={locationChanges.data} />
          )}
        </Stack>
      )}
    </Page>
  )
}
